"use client";

import React, { useEffect, useMemo, useState } from "react";

export type ClipartItem = {
  id: string;
  style: string;
  category: string;
  title: string;
  fileName: string;
  path: string;
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
  fileSize: number;
  kind: "clipart" | "sheet";
  mimeType: string;
};

type ClipartCategory = {
  id: string;
  style: string;
  category: string;
  title: string;
  count: number;
};

type ClipartManifest = {
  styles: string[];
  categories: ClipartCategory[];
  items: ClipartItem[];
};

function titleCase(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function ClipartLibraryPanel({
  locale,
  readOnly,
  onInsertClipart,
}: {
  locale: string;
  readOnly?: boolean;
  onInsertClipart?: (item: ClipartItem) => void;
}) {
  const isPl = locale === "pl";
  const [manifest, setManifest] = useState<ClipartManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeStyle, setActiveStyle] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch("/clipart/manifest.json")
      .then((res) => {
        if (!res.ok) throw new Error(`clipart manifest ${res.status}`);
        return res.json();
      })
      .then((data: ClipartManifest) => {
        if (cancelled) return;
        const styles = Array.isArray(data.styles) ? data.styles : [];
        const categories = Array.isArray(data.categories) ? data.categories : [];
        setManifest({ ...data, styles, categories, items: Array.isArray(data.items) ? data.items : [] });
        setActiveStyle(styles[0] || "");
        setActiveCategory(categories.find((cat) => cat.style === styles[0])?.id || categories[0]?.id || "");
      })
      .catch((err) => {
        if (!cancelled) setError(String(err?.message || err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const styles = manifest?.styles || [];
  const categories = useMemo(
    () => (manifest?.categories || []).filter((cat) => !activeStyle || cat.style === activeStyle),
    [activeStyle, manifest?.categories]
  );
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (manifest?.items || [])
      .filter((item) => (!activeStyle || item.style === activeStyle) && (!activeCategory || `${item.style}/${item.category}` === activeCategory))
      .filter((item) => !q || item.title.toLowerCase().includes(q) || item.category.toLowerCase().includes(q))
      .slice(0, 120);
  }, [activeCategory, activeStyle, manifest?.items, query]);

  useEffect(() => {
    if (!categories.length) {
      setActiveCategory("");
      return;
    }
    if (!categories.some((cat) => cat.id === activeCategory)) {
      setActiveCategory(categories[0].id);
    }
  }, [activeCategory, categories]);

  if (error) {
    return <div className="rounded-xl border border-red-300/30 bg-red-950/25 p-3 text-sm text-red-100">{error}</div>;
  }

  if (!manifest) {
    return <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-[#98a4bc]">{isPl ? "Ładowanie clipartów..." : "Loading clipart..."}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {styles.map((style) => (
          <button
            key={style}
            type="button"
            className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              activeStyle === style ? "bg-white text-[#272727]" : "bg-white/6 text-white/80 hover:bg-white/10"
            }`}
            onClick={() => {
              setActiveStyle(style);
              setActiveCategory(manifest.categories.find((cat) => cat.style === style)?.id || "");
            }}
          >
            {titleCase(style)}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              activeCategory === cat.id
                ? "border-white/40 bg-white text-[#272727]"
                : "border-white/10 bg-white/5 text-white/75 hover:bg-white/10"
            }`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.title} <span className="opacity-60">{cat.count}</span>
          </button>
        ))}
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-white/30"
        placeholder={isPl ? "Szukaj w tej kategorii" : "Search this category"}
      />

      <div className="grid grid-cols-3 gap-2">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className="group aspect-square overflow-hidden rounded-xl border border-white/10 bg-white transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onInsertClipart?.(item)}
            disabled={readOnly}
            title={item.title}
            aria-label={item.title}
          >
            <img
              src={item.thumbUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-contain p-1 transition-transform group-hover:scale-105"
            />
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-[#98a4bc]">
          {isPl ? "Brak clipartów w tej kategorii." : "No clipart in this category."}
        </div>
      ) : null}
    </div>
  );
}
